var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
import { State, Action, Selector } from '@ngxs/store';
import { EngineStateModel } from './engine.state.model';
import { Load, LoadSuccess, SetReady, SetGraph, SetEngineChapter, SetEnginePosition, SetEngineChapterInit, SynchronizeVisibleNodes, ClearGraph, ResetEnginePosition, ToggleCameraBar, ToggleLabels, SetActiveTool, PlayAnimation, CenterCameraOnVisible, SetDisplay, SetVisible, SynchronizeRenderableNodes, SynchronizePickableNodes, SetScrollMode, SetXrayMode, SetXrayModeCacheColorRecipes, } from './engine.actions';
import { BdGraphService } from '../../../lib/graph/graph.service';
import { EngineService } from '../engine.service';
import { BdGraphFactory } from 'src/lib/graph/graph.model';
import { EnginePositionModel } from '../models/engine-position.model';
import { EngineVisibilityEnum } from '../models/engine-visibility.enum';
import { EngineCameraState } from './camera/engine.camera.state';
import { ToolEnum } from '../models/tool.enum';
import { ScrollModeEnum } from '../models/scroll-mode.enum';
import { SetObjectLayerOrder } from './engine.actions';
const enginePositionDefaults = new EnginePositionModel(0, 0, 0, 0, EngineVisibilityEnum.Hidden);
let EngineState = class EngineState {
    constructor(graphService, engineService) {
        this.graphService = graphService;
        this.engineService = engineService;
    }
    /**
     * Selectors
     */
    static getEngine(state) {
        return state.engine;
    }
    static getReady(state) {
        return state.ready;
    }
    static getDisplay(state) {
        return state.display;
    }
    static getScrollMode(state) {
        return state.scrollMode;
    }
    static getVisible(state) {
        return state.visible;
    }
    static getViewGraph(state) {
        return state.viewGraph;
    }
    static getViewGraphNode(state) {
        return (objectId) => {
            return state.viewGraph.nodes[objectId];
        };
    }
    static getEnginePosition(state) {
        return state.enginePosition;
    }
    static getChapter(state) {
        return state.chapter;
    }
    static getActiveTool(state) {
        return state.activeTool;
    }
    static getShowCameraBar(state) {
        return state.showCameraBar;
    }
    static getShowLabels(state) {
        return state.showLabels;
    }
    static getChapterInit(state) {
        return state.chapterInit;
    }
    static getAnimationPlaying(state) {
        return state.animationPlaying;
    }
    static getXrayModeEnabled(state) {
        return state.xrayModeEnabled;
    }
    static getXrayCachedColorRecipes(state) {
        return state.xrayModeCachedColorRecipes;
    }
    /**
     * Actions
     */
    load(context, action) {
        context.dispatch(new LoadSuccess('engine'));
    }
    loadSuccess({ patchState }, action) {
        patchState({
            engine: action.result
        });
    }
    setReady(context, action) {
        context.patchState({
            ready: action.ready
        });
    }
    setDisplay(context, action) {
        context.patchState({
            display: action.display
        });
    }
    setZoomViaScrollAllowed(context, action) {
        context.patchState({
            scrollMode: action.scrollMode
        });
    }
    setVisible(context, action) {
        context.patchState({
            visible: action.visible
        });
    }
    setEnginePosition(context, action) {
        context.patchState({
            enginePosition: Object.assign({}, action.position, { right: action.position.left + action.position.width, bottom: action.position.top + action.position.height })
        });
    }
    resetEnginePosition(context, action) {
        context.patchState({
            enginePosition: enginePositionDefaults
        });
    }
    setChapter(context, action) {
        context.patchState({
            chapter: action.chapterId
        });
    }
    setChapterInit(context, action) {
        context.patchState({
            chapterInit: action.chapterInit
        });
    }
    setActiveTool(context, action) {
        context.patchState({
            activeTool: action.tool
        });
    }
    /**
     * Graph Actions #TODO: Move into own state
     */
    setGraph(context, action) {
        // Set graph state
        context.patchState({
            viewGraph: Object.assign({}, action.graph)
        });
    }
    clearGraph(context, action) {
        context.patchState({
            viewGraph: BdGraphFactory()
        });
    }
    setXrayMode(context, action) {
        context.patchState({
            xrayModeEnabled: action.enabled
        });
    }
    setXrayModeCacheColorRecipes(context, action) {
        context.patchState({
            xrayModeCachedColorRecipes: [...action.cachedColorRecipes]
        });
    }
    centerCameraOnVisible(context, action) {
        const visibleLeaves = this.engineService.getVisibleLeaves();
        const boundary = this.engineService.getBoundaryFromObjects(visibleLeaves);
        const lookAt = this.engineService.getCameraLookAt();
        lookAt.look = {
            x: (boundary.xmin + boundary.xmax) / 2.0,
            y: (boundary.ymin + boundary.ymax) / 2.0,
            z: (boundary.zmin + boundary.zmax) / 2.0
        };
        Human.renderer.camera.fly.flyTo(lookAt);
    }
    toggleCameraBar(context, action) {
        context.patchState({
            showCameraBar: action.show
        });
    }
    toggleLabels(context, action) {
        const show = (action.show != null) ? action.show : !context.getState().showLabels;
        context.patchState({
            showLabels: show
        });
    }
    synchronizeVisibleNodes(context, action) {
        const viewGraph = context.getState().viewGraph;
        const leafViewNodeIds = this.stateMapToNodeIds(viewGraph, action.leafVisibilityState);
        // set unique view node leaf state by querying corresponding human object state
        const leafState = {};
        leafViewNodeIds.forEach(viewNodeId => {
            const leafids = this.engineService.getLeafObjects(viewNodeId);
            const visibility = leafids.reduce((vis, objectId) => vis || Human.scene.objects[objectId].shown, false);
            leafState[viewNodeId] = { 'shown': visibility };
        });
        this.graphService.synchronize(viewGraph, { 'shown': 'trueWins' }, leafState);
        // patch the state
        context.patchState({
            viewGraph: Object.assign({}, viewGraph)
        });
    }
    synchronizeRenderableNodes(context, action) {
        const viewGraph = context.getState().viewGraph;
        const leafViewNodeIds = this.stateMapToNodeIds(viewGraph, action.leafRenderableState);
        // view graph leaf state
        const leafState = {};
        leafViewNodeIds.forEach(leafId => {
            leafState[leafId] = { 'renderable': true };
        });
        this.graphService.synchronize(viewGraph, { 'renderable': 'falseWins' }, leafState);
    }
    synchronizePickableNodes(context, action) {
        const viewGraph = context.getState().viewGraph;
        const leafViewNodeIds = this.stateMapToNodeIds(viewGraph, action.leafPickableState);
        // set unique view node leaf state by querying corresponding human object state
        const leafState = {};
        leafViewNodeIds.forEach(viewNodeId => {
            const leafids = this.engineService.getLeafObjects(viewNodeId);
            const pickable = leafids.reduce((pick, objectId) => pick || Human.scene.objects[objectId].pickable, false);
            leafState[viewNodeId] = { 'pickable': pickable };
        });
        this.graphService.synchronize(viewGraph, { 'pickable': 'trueWins' }, leafState);
        // patch the state
        context.patchState({
            viewGraph: Object.assign({}, viewGraph)
        });
    }
    playAnimation(context, action) {
        context.patchState({
            animationPlaying: action.play
        });
    }
    setObjectLayerOrder(context, action) {
        const viewGraph = context.getState().viewGraph;
        const layer = isNaN(action.layer) ? 0 : action.layer;
        let nodeIds = [];
        action.nodes.forEach((node) => {
            const descendantIds = this.graphService.getDescendants(viewGraph, node.id, true);
            nodeIds = nodeIds.concat(descendantIds);
        });
        nodeIds.forEach((nodeId) => {
            this.engineService.setObjectLayer(nodeId, layer);
            this.graphService.setState(viewGraph, nodeId, { layer: layer });
        });
        // patch the state
        context.patchState({
            viewGraph: Object.assign({}, viewGraph)
        });
    }
    stateMapToNodeIds(viewGraph, stateMap) {
        // map human objects to relevant view nodes (if any);
        const ids = Object.keys(stateMap)
            .map(objectId => this.engineService.getNode(viewGraph, objectId))
            .filter(node => !!node)
            .map(node => node.id);
        return new Set(ids);
    }
};
__decorate([
    Action(Load),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object, Load]),
    __metadata("design:returntype", void 0)
], EngineState.prototype, "load", null);
__decorate([
    Action(LoadSuccess),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object, LoadSuccess]),
    __metadata("design:returntype", void 0)
], EngineState.prototype, "loadSuccess", null);
__decorate([
    Action(SetReady),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object, SetReady]),
    __metadata("design:returntype", void 0)
], EngineState.prototype, "setReady", null);
__decorate([
    Action(SetDisplay),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object, SetDisplay]),
    __metadata("design:returntype", void 0)
], EngineState.prototype, "setDisplay", null);
__decorate([
    Action(SetScrollMode),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object, SetScrollMode]),
    __metadata("design:returntype", void 0)
], EngineState.prototype, "setZoomViaScrollAllowed", null);
__decorate([
    Action(SetVisible),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object, SetVisible]),
    __metadata("design:returntype", void 0)
], EngineState.prototype, "setVisible", null);
__decorate([
    Action(SetEnginePosition),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object, SetEnginePosition]),
    __metadata("design:returntype", void 0)
], EngineState.prototype, "setEnginePosition", null);
__decorate([
    Action(ResetEnginePosition),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object, ResetEnginePosition]),
    __metadata("design:returntype", void 0)
], EngineState.prototype, "resetEnginePosition", null);
__decorate([
    Action(SetEngineChapter),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object, SetEngineChapter]),
    __metadata("design:returntype", void 0)
], EngineState.prototype, "setChapter", null);
__decorate([
    Action(SetEngineChapterInit),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object, SetEngineChapterInit]),
    __metadata("design:returntype", void 0)
], EngineState.prototype, "setChapterInit", null);
__decorate([
    Action(SetActiveTool),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object, SetActiveTool]),
    __metadata("design:returntype", void 0)
], EngineState.prototype, "setActiveTool", null);
__decorate([
    Action(SetGraph),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object, SetGraph]),
    __metadata("design:returntype", void 0)
], EngineState.prototype, "setGraph", null);
__decorate([
    Action(ClearGraph),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object, SetGraph]),
    __metadata("design:returntype", void 0)
], EngineState.prototype, "clearGraph", null);
__decorate([
    Action(SetXrayMode),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object, SetXrayMode]),
    __metadata("design:returntype", void 0)
], EngineState.prototype, "setXrayMode", null);
__decorate([
    Action(SetXrayModeCacheColorRecipes),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object, SetXrayModeCacheColorRecipes]),
    __metadata("design:returntype", void 0)
], EngineState.prototype, "setXrayModeCacheColorRecipes", null);
__decorate([
    Action(CenterCameraOnVisible),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object, CenterCameraOnVisible]),
    __metadata("design:returntype", void 0)
], EngineState.prototype, "centerCameraOnVisible", null);
__decorate([
    Action(ToggleCameraBar),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object, ToggleCameraBar]),
    __metadata("design:returntype", void 0)
], EngineState.prototype, "toggleCameraBar", null);
__decorate([
    Action(ToggleLabels),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object, ToggleLabels]),
    __metadata("design:returntype", void 0)
], EngineState.prototype, "toggleLabels", null);
__decorate([
    Action(SynchronizeVisibleNodes),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object, SynchronizeVisibleNodes]),
    __metadata("design:returntype", void 0)
], EngineState.prototype, "synchronizeVisibleNodes", null);
__decorate([
    Action(SynchronizeRenderableNodes),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object, SynchronizeRenderableNodes]),
    __metadata("design:returntype", void 0)
], EngineState.prototype, "synchronizeRenderableNodes", null);
__decorate([
    Action(SynchronizePickableNodes),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object, SynchronizePickableNodes]),
    __metadata("design:returntype", void 0)
], EngineState.prototype, "synchronizePickableNodes", null);
__decorate([
    Action(PlayAnimation),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object, PlayAnimation]),
    __metadata("design:returntype", void 0)
], EngineState.prototype, "playAnimation", null);
__decorate([
    Action(SetObjectLayerOrder),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object, SetObjectLayerOrder]),
    __metadata("design:returntype", void 0)
], EngineState.prototype, "setObjectLayerOrder", null);
__decorate([
    Selector(),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [EngineStateModel]),
    __metadata("design:returntype", void 0)
], EngineState, "getEngine", null);
__decorate([
    Selector(),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [EngineStateModel]),
    __metadata("design:returntype", void 0)
], EngineState, "getReady", null);
__decorate([
    Selector(),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [EngineStateModel]),
    __metadata("design:returntype", void 0)
], EngineState, "getDisplay", null);
__decorate([
    Selector(),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [EngineStateModel]),
    __metadata("design:returntype", void 0)
], EngineState, "getScrollMode", null);
__decorate([
    Selector(),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [EngineStateModel]),
    __metadata("design:returntype", void 0)
], EngineState, "getVisible", null);
__decorate([
    Selector(),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [EngineStateModel]),
    __metadata("design:returntype", void 0)
], EngineState, "getViewGraph", null);
__decorate([
    Selector(),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [EngineStateModel]),
    __metadata("design:returntype", void 0)
], EngineState, "getViewGraphNode", null);
__decorate([
    Selector(),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [EngineStateModel]),
    __metadata("design:returntype", void 0)
], EngineState, "getEnginePosition", null);
__decorate([
    Selector(),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [EngineStateModel]),
    __metadata("design:returntype", void 0)
], EngineState, "getChapter", null);
__decorate([
    Selector(),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [EngineStateModel]),
    __metadata("design:returntype", void 0)
], EngineState, "getActiveTool", null);
__decorate([
    Selector(),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [EngineStateModel]),
    __metadata("design:returntype", void 0)
], EngineState, "getShowCameraBar", null);
__decorate([
    Selector(),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [EngineStateModel]),
    __metadata("design:returntype", void 0)
], EngineState, "getShowLabels", null);
__decorate([
    Selector(),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [EngineStateModel]),
    __metadata("design:returntype", void 0)
], EngineState, "getChapterInit", null);
__decorate([
    Selector(),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [EngineStateModel]),
    __metadata("design:returntype", void 0)
], EngineState, "getAnimationPlaying", null);
__decorate([
    Selector(),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [EngineStateModel]),
    __metadata("design:returntype", void 0)
], EngineState, "getXrayModeEnabled", null);
__decorate([
    Selector(),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [EngineStateModel]),
    __metadata("design:returntype", void 0)
], EngineState, "getXrayCachedColorRecipes", null);
EngineState = __decorate([
    State({
        name: 'engine',
        defaults: {
            loading: false,
            ready: false,
            viewGraph: BdGraphFactory(),
            engine: '',
            chapter: '',
            chapterInit: {
                id: '',
                init: false,
                camera: false,
                annotations: false,
                graph: false,
                properties: false,
                particles: false,
                clips: false,
                postprocessing: false,
                transparency: false
            },
            showCameraBar: true,
            showLabels: true,
            activeTool: ToolEnum.Select,
            animationPlaying: true,
            display: true,
            visible: true,
            scrollMode: ScrollModeEnum.Capture,
            xrayModeEnabled: false,
            xrayModeCachedColorRecipes: []
        },
        children: [
            EngineCameraState
        ]
    }),
    __metadata("design:paramtypes", [BdGraphService,
        EngineService])
], EngineState);
export { EngineState };
